import { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCreateOrdine, useUpdateOrdine, useOrdine, useSalvaComeBox } from '@/hooks/useOrdini'
import { useConfigurazioniScatolaAttive, useTipologieBoxAttive, useTipologiaBox } from '@/hooks/useBox'
import { useProdottiAttivi } from '@/hooks/useProdotti'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { 
  ArrowLeft, ArrowRight, Check, User, Package, 
  ShoppingCart, FileText, Search, Plus, Minus, Trash2
} from 'lucide-react'
import { BoxSelector, ProdottoSelector } from '@/components/ordini'
import ClienteAutocomplete from '@/components/clienti/ClienteAutocomplete'
import ClienteQuickCreateDialog from '@/components/clienti/ClienteQuickCreateDialog'
import VenditoreSelector from '@/components/venditori/VenditoreSelector'
import { toast } from 'sonner'
import { centsToEuroInput, euroToCents } from '@/utils/euroAmount'
import {
  orderWizardDraftKey, readOrderWizardDraft, saveOrderWizardDraft, clearOrderWizardDraft,
} from '@/utils/orderWizardDraft'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'

const STEPS = [
  { id: 1, title: 'Cliente', icon: User },
  { id: 2, title: 'Tipo Ordine', icon: Package },
  { id: 3, title: 'Prodotti', icon: ShoppingCart },
  { id: 4, title: 'Riepilogo', icon: FileText },
]

const TIPI_ORDINE = [
  { 
    id: 1, 
    nome: 'Box da Catalogo', 
    descrizione: 'Scegli box predefinite dal catalogo con composizione fissa',
    icon: '📦'
  },
  { 
    id: 2, 
    nome: 'Box Personalizzata', 
    descrizione: 'Componi una box personalizzata scegliendo i prodotti',
    icon: '🎁'
  },
]

const INITIAL_FORM_DATA = {
  cliente: null,
  venditore: null,
  indirizzo_spedizione: null,
  tipo: 1,
  configurazione_scatola: null,
  template_box: null,
  righe: [],
  quantita_box: 1,
  costo_scatola_euro: null,
  prezzo_finale_euro: null,
  spedizione_euro: '0,00',
  sconto_importo_cents: 0,
  totale_finale_euro: null,
  note: '',
  note_interne: '',
  data_consegna_richiesta_dal: '',
  data_consegna_richiesta_al: '',
  richiede_brandizzazione: false,
  note_brandizzazione: '',
}

export default function OrdineWizard() {
  const navigate = useNavigate()
  const { id } = useParams() // ID ordine per modalità edit
  const isEditMode = !!id
  const draftKey = orderWizardDraftKey(id)
  
  const createMutation = useCreateOrdine()
  const updateMutation = useUpdateOrdine()
  const salvaComeBoxMutation = useSalvaComeBox()
  
  // Fetch ordine esistente in modalità edit
  const { data: ordineEsistente, isLoading: loadingOrdine } = useOrdine(id, {
    enabled: isEditMode,
  })
  
  const [step, setStep] = useState(1)
  const [pendingDraft, setPendingDraft] = useState(() => readOrderWizardDraft(draftKey))
  const [draftReady, setDraftReady] = useState(() => !readOrderWizardDraft(draftKey))
  const [pendingReset, setPendingReset] = useState(null)
  const [createClienteDialogOpen, setCreateClienteDialogOpen] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const prezziPrimaDelCambioScatola = useRef(null)
  const initialEditForm = useRef(null)
  
  const [formData, setFormData] = useState(INITIAL_FORM_DATA)
  
  // Pre-popola formData quando ordine esistente viene caricato
  useEffect(() => {
    if (isEditMode && ordineEsistente && !isInitialized) {
      // Costruisci oggetto cliente con dati necessari
      const clienteObj = ordineEsistente.cliente ? {
        id: ordineEsistente.cliente.id || ordineEsistente.cliente,
        nome_completo: ordineEsistente.cliente_nome,
        email: ordineEsistente.cliente_email,
        telefono: ordineEsistente.cliente_telefono,
        indirizzi: ordineEsistente.cliente?.indirizzi || [],
      } : null
      
      // Costruisci oggetto venditore
      const venditoreObj = ordineEsistente.venditore ? {
        id: typeof ordineEsistente.venditore === 'object' ? ordineEsistente.venditore.id : ordineEsistente.venditore,
        nome_completo: ordineEsistente.venditore_nome,
      } : null
      
      // Mappa righe ordine esistenti
      let righeEsistenti = []
      let quantitaBoxEsistente = 1
      let scontoRigaBoxPercentuale = 0
      
      // Per ordini Tipo 2 (Box Personalizzata) con tipologia_box, espandi la composizione
      let templateBoxId = null
      
      if (ordineEsistente.tipo === 2 && ordineEsistente.righe?.length > 0) {
        const rigaBox = ordineEsistente.righe[0]
        
        if (rigaBox.tipologia_box && rigaBox.tipologia_box_data?.composizione) {
          // Salva ID template box per pre-selezionarlo
          templateBoxId = rigaBox.tipologia_box
          
          // Salva numero di box ordinate
          quantitaBoxEsistente = rigaBox.quantita || 1
          scontoRigaBoxPercentuale = Number(rigaBox.sconto_percentuale || 0)
          
          // Espandi composizione box in righe prodotto
          righeEsistenti = rigaBox.tipologia_box_data.composizione.map(comp => ({
              prodotto: comp.prodotto,
              prodotto_data: {
                id: comp.prodotto,
                nome: comp.prodotto_nome,
                codice: comp.prodotto_codice,
                prezzo_vendita_cents: comp.prezzo_unitario_cents,
                prezzo_acquisto_cents: comp.costo_unitario_cents,
                immagine_url: comp.prodotto_immagine_url,
              },
              quantita: comp.quantita,
              prezzo_unitario_cents: comp.prezzo_unitario_cents,
          }))
        } else {
          // Righe prodotto normali
          righeEsistenti = ordineEsistente.righe.map(riga => ({
            id: riga.id,
            prodotto: riga.prodotto,
            prodotto_data: riga.prodotto_data,
            quantita: riga.quantita,
            prezzo_unitario_cents: riga.prezzo_unitario_cents,
            sconto_percentuale: riga.sconto_percentuale,
          }))
        }
      } else {
        // Per Tipo 1 o altre situazioni, carica righe normalmente
        righeEsistenti = (ordineEsistente.righe || []).map(riga => ({
          id: riga.id,
          tipologia_box: riga.tipologia_box,
          tipologia_box_data: riga.tipologia_box_data,
          prodotto: riga.prodotto,
          prodotto_data: riga.prodotto_data,
          quantita: riga.quantita,
          prezzo_unitario_cents: riga.prezzo_unitario_cents,
          sconto_percentuale: riga.sconto_percentuale,
        }))
      }
      
      const editForm = {
        cliente: clienteObj,
        venditore: venditoreObj,
        indirizzo_spedizione: ordineEsistente.indirizzo_spedizione,
        tipo: ordineEsistente.tipo,
        configurazione_scatola: ordineEsistente.configurazione_scatola,
        template_box: templateBoxId, // Pre-seleziona template box esistente
        righe: righeEsistenti,
        quantita_box: quantitaBoxEsistente,
        sconto_riga_box_percentuale: scontoRigaBoxPercentuale,
        costo_scatola_euro: ordineEsistente.tipo === 2
          ? centsToEuroInput(ordineEsistente.righe?.[0]?.tipologia_box_data?.costo_scatola_cents)
          : null,
        prezzo_finale_euro: ordineEsistente.tipo === 2
          ? centsToEuroInput(ordineEsistente.righe?.[0]?.prezzo_unitario_cents)
          : null,
        spedizione_euro: centsToEuroInput(ordineEsistente.totale_spedizione_cents ?? 0),
        sconto_importo_cents: ordineEsistente.totale_sconti_cents ?? 0,
        totale_finale_euro: null,
        note: ordineEsistente.note || '',
        note_interne: ordineEsistente.note_interne || '',
        data_consegna_richiesta_dal: ordineEsistente.data_consegna_richiesta_dal || '',
        data_consegna_richiesta_al: ordineEsistente.data_consegna_richiesta_al || '',
        richiede_brandizzazione: ordineEsistente.richiede_brandizzazione || false,
        note_brandizzazione: ordineEsistente.note_brandizzazione || '',
      }
      initialEditForm.current = editForm
      setFormData(editForm)
      setStep(4)
      if (pendingDraft && pendingDraft.sourceUpdatedAt !== ordineEsistente.updated_at) {
        clearOrderWizardDraft(draftKey)
        setPendingDraft(null)
        setDraftReady(true)
      }
      setIsInitialized(true)
    }
  }, [isEditMode, ordineEsistente, isInitialized, pendingDraft, draftKey])
  
  // Stato per "Salva come TipologiaBox"
  const [salvaComeBox, setSalvaComeBox] = useState(false)
  const [nomeBox, setNomeBox] = useState('')
  const [codiceBox, setCodiceBox] = useState('')

  useEffect(() => {
    if (!draftReady || (isEditMode && !isInitialized)) return
    const hasContent = Boolean(
      formData.cliente || formData.venditore || formData.righe.length ||
      formData.note || formData.note_interne || formData.configurazione_scatola ||
      (euroToCents(formData.spedizione_euro) || 0) > 0 ||
      Number(formData.sconto_importo_cents || 0) > 0 || formData.totale_finale_euro !== null ||
      step > 1 || salvaComeBox || nomeBox || codiceBox
    )
    if (!isEditMode && !hasContent) {
      clearOrderWizardDraft(draftKey)
      return
    }
    if (isEditMode && step === 4 &&
        JSON.stringify(formData) === JSON.stringify(initialEditForm.current) &&
        !salvaComeBox && !nomeBox && !codiceBox) {
      clearOrderWizardDraft(draftKey)
      return
    }
    saveOrderWizardDraft(draftKey, {
      formData, step, salvaComeBox, nomeBox, codiceBox,
      sourceUpdatedAt: isEditMode ? ordineEsistente?.updated_at : null,
    })
  }, [draftReady, isEditMode, isInitialized, draftKey, formData, step, salvaComeBox, nomeBox, codiceBox, ordineEsistente?.updated_at])

  const resumeDraft = () => {
    setFormData({ ...INITIAL_FORM_DATA, ...pendingDraft.formData })
    setStep(Math.min(4, Math.max(1, Number(pendingDraft.step) || 1)))
    setSalvaComeBox(Boolean(pendingDraft.salvaComeBox))
    setNomeBox(pendingDraft.nomeBox || '')
    setCodiceBox(pendingDraft.codiceBox || '')
    setPendingDraft(null)
    setDraftReady(true)
  }

  const restartWizard = () => {
    clearOrderWizardDraft(draftKey)
    setFormData(isEditMode ? initialEditForm.current : { ...INITIAL_FORM_DATA })
    setStep(isEditMode ? 4 : 1)
    setSalvaComeBox(false)
    setNomeBox('')
    setCodiceBox('')
    setPendingDraft(null)
    setDraftReady(true)
    setPendingReset(null)
  }

  // I cataloghi servono solo nella composizione: il riepilogo in modifica usa
  // i dati già restituiti dall'ordine, evitando richieste e pagine inutili.
  const loadBoxCatalogs = step === 3 && formData.tipo === 2
  const { data: configurazioniData } = useConfigurazioniScatolaAttive({ enabled: loadBoxCatalogs })
  const configurazioni = configurazioniData?.results || configurazioniData || []
  
  const { data: tipologieData } = useTipologieBoxAttive({ enabled: loadBoxCatalogs })
  const tipologieBox = tipologieData?.results || tipologieData || []
  
  const { data: prodottiData } = useProdottiAttivi({ enabled: loadBoxCatalogs })
  const prodotti = prodottiData?.results || prodottiData || []
  
  // Carica dettaglio template box selezionato
  const { data: templateBoxDetail } = useTipologiaBox(formData.template_box, {
    enabled: step === 3 && !isEditMode,
  })
  
  // Arricchisci righe con prodotto_data quando manca (per edit mode)
  useEffect(() => {
    if (isEditMode && prodotti.length > 0 && formData.righe.length > 0) {
      const righeArricchite = formData.righe.map(riga => {
        // Se prodotto_data manca o è incompleto, cerca il prodotto nella lista
        if (riga.prodotto && (!riga.prodotto_data || !riga.prodotto_data.nome)) {
          const prodotto = prodotti.find(p => p.id === riga.prodotto)
          if (prodotto) {
            return {
              ...riga,
              prodotto_data: prodotto
            }
          }
        }
        return riga
      })
      
      // Aggiorna solo se ci sono cambiamenti
      const hasChanges = righeArricchite.some((riga, index) => 
        riga.prodotto_data !== formData.righe[index].prodotto_data
      )
      
      if (hasChanges) {
        setFormData(prev => ({ ...prev, righe: righeArricchite }))
      }
    }
  }, [prodotti, isEditMode, formData.righe])
  
  // Filtra tipologie box per configurazione scatola selezionata
  const tipologiePerScatola = useMemo(() => {
    if (!formData.configurazione_scatola) return []
    return tipologieBox.filter(t => t.configurazione === formData.configurazione_scatola)
  }, [tipologieBox, formData.configurazione_scatola])

  const handleSelectCliente = (cliente) => {
    setFormData(prev => ({
      ...prev,
      cliente,
      indirizzo_spedizione: cliente.indirizzi?.find(i => i.default)?.id || null
    }))
  }

  const applyTipo = (tipo) => {
    setFormData(prev => ({
      ...prev, tipo, configurazione_scatola: null, righe: [], template_box: null,
      quantita_box: 1, costo_scatola_euro: null, prezzo_finale_euro: null,
    }))
    setSalvaComeBox(false)
    setNomeBox('')
    setCodiceBox('')
  }

  const handleSelectTipo = (tipo) => {
    if (isEditMode || formData.tipo === tipo) return
    if (formData.righe.length > 0 || formData.configurazione_scatola) {
      setPendingReset({ kind: 'tipo', value: tipo })
    } else {
      applyTipo(tipo)
    }
  }

  const applyConfigurazione = (configurazione) => {
    setFormData(prev => ({
      ...prev, configurazione_scatola: configurazione, template_box: null,
      righe: [], costo_scatola_euro: null, prezzo_finale_euro: null,
    }))
    setSalvaComeBox(false)
  }

  const handleSelectConfigurazione = (configurazione) => {
    if (isEditMode || formData.configurazione_scatola === configurazione) return
    if (formData.righe.length > 0) {
      setPendingReset({ kind: 'configurazione', value: configurazione })
    } else {
      applyConfigurazione(configurazione)
    }
  }

  const applyTemplate = (templateId) => {
    setFormData(prev => ({
      ...prev, template_box: templateId, righe: templateId ? [] : prev.righe,
      prezzo_finale_euro: null, costo_scatola_euro: null,
    }))
  }

  const handleSelectTemplateBox = (templateId) => {
    if (isEditMode || formData.template_box === templateId) return
    if (formData.righe.length > 0) {
      setPendingReset({ kind: 'template', value: templateId })
    } else {
      applyTemplate(templateId)
    }
  }

  const confirmReset = () => {
    if (pendingReset?.kind === 'tipo') applyTipo(pendingReset.value)
    if (pendingReset?.kind === 'configurazione') applyConfigurazione(pendingReset.value)
    if (pendingReset?.kind === 'template') applyTemplate(pendingReset.value)
    if (pendingReset?.kind === 'restart') restartWizard()
    setPendingReset(null)
  }
  
  // Precompila prodotti quando viene caricato il dettaglio del template
  useEffect(() => {
    if (!isEditMode && draftReady && templateBoxDetail?.id === formData.template_box && formData.template_box && formData.righe.length === 0) {
      const righeProdotti = templateBoxDetail.composizione?.map(comp => ({
        prodotto: comp.prodotto,
        quantita: comp.quantita,
        prezzo_unitario_cents: comp.prezzo_unitario_cents || 0,
        prodotto_data: {
          id: comp.prodotto,
          nome: comp.prodotto_nome,
          codice: comp.prodotto_codice,
          prezzo_vendita_cents: comp.prezzo_unitario_cents,
          prezzo_acquisto_cents: comp.costo_unitario_cents,
        },
      })) || []
      
      if (righeProdotti.length > 0) {
        setFormData(prev => ({
          ...prev,
          righe: righeProdotti,
          costo_scatola_euro: templateBoxDetail.costo_scatola_override_cents == null
            ? null : centsToEuroInput(templateBoxDetail.costo_scatola_override_cents),
          prezzo_finale_euro: centsToEuroInput(templateBoxDetail.prezzo_vendita_cents),
        }))
      }
    }
  }, [templateBoxDetail, formData.template_box, formData.righe.length, isEditMode, draftReady])

  // Handlers per BoxSelector
  const handleSelectBox = (item) => {
    setFormData(prev => ({
      ...prev,
      righe: [...prev.righe, item]
    }))
  }

  const handleUpdateBoxQuantity = (tipologiaBoxId, quantita) => {
    setFormData(prev => ({
      ...prev,
      righe: prev.righe.map(riga => 
        riga.tipologia_box === tipologiaBoxId ? { ...riga, quantita } : riga
      )
    }))
  }

  const handleRemoveBox = (tipologiaBoxId) => {
    setFormData(prev => ({
      ...prev,
      righe: prev.righe.filter(riga => riga.tipologia_box !== tipologiaBoxId)
    }))
  }

  // Handlers per ProdottoSelector
  const handleSelectProdotto = (item) => {
    setFormData(prev => ({
      ...prev,
      righe: [...prev.righe, item]
    }))
  }

  const handleUpdateProdottoQuantity = (prodottoId, quantita) => {
    setFormData(prev => ({
      ...prev,
      righe: prev.righe.map(riga => 
        riga.prodotto === prodottoId ? { ...riga, quantita } : riga
      )
    }))
  }

  const handleRemoveProdotto = (prodottoId) => {
    setFormData(prev => ({
      ...prev,
      righe: prev.righe.filter(riga => riga.prodotto !== prodottoId)
    }))
  }

  const configurazioneSelezionata = configurazioni.find(c => c.id === formData.configurazione_scatola)
  const costoScatolaDefaultCents = configurazioneSelezionata?.costo_totale_cents || 0
  const costoScatolaInput = formData.costo_scatola_euro ?? centsToEuroInput(costoScatolaDefaultCents)
  const costoScatolaCents = euroToCents(costoScatolaInput)
  const costoContenutoCents = formData.righe.reduce((totale, riga) => {
    if (!riga.prodotto) return totale
    const prodotto = prodotti.find(p => p.id === riga.prodotto) || riga.prodotto_data
    return totale + (Number(prodotto?.prezzo_acquisto_cents || 0) * riga.quantita)
  }, 0)
  const costoTotaleCents = costoScatolaCents === null ? null : costoScatolaCents + costoContenutoCents
  const prezzoProdottiCents = formData.righe.reduce((totale, riga) => {
    if (!riga.prodotto) return totale
    const prodotto = prodotti.find(p => p.id === riga.prodotto) || riga.prodotto_data
    return totale + (Number(prodotto?.prezzo_vendita_cents ?? riga.prezzo_unitario_cents ?? 0) * riga.quantita)
  }, 0)
  const prezzoFinaleInput = formData.prezzo_finale_euro ?? centsToEuroInput(prezzoProdottiCents + (costoScatolaCents ?? 0))
  const prezzoFinaleCents = euroToCents(prezzoFinaleInput)
  const margineCents = prezzoFinaleCents === null || costoTotaleCents === null
    ? null : prezzoFinaleCents - costoTotaleCents

  const handleCostoScatolaFocus = () => {
    if (costoScatolaCents !== null && prezzoFinaleCents !== null) {
      prezziPrimaDelCambioScatola.current = {
        costo: costoScatolaCents,
        finale: prezzoFinaleCents,
      }
    }
  }

  const handleCostoScatolaChange = (value) => {
    if (!prezziPrimaDelCambioScatola.current) handleCostoScatolaFocus()
    setFormData(prev => ({ ...prev, costo_scatola_euro: value }))
  }

  const handleCostoScatolaBlur = (value) => {
    const nuovoCosto = euroToCents(value)
    if (nuovoCosto === null) return

    const prezziPrecedenti = prezziPrimaDelCambioScatola.current
    prezziPrimaDelCambioScatola.current = null
    setFormData(prev => ({
      ...prev,
      costo_scatola_euro: centsToEuroInput(nuovoCosto),
      prezzo_finale_euro: prezziPrecedenti
        ? centsToEuroInput(Math.max(0, prezziPrecedenti.finale + nuovoCosto - prezziPrecedenti.costo))
        : prev.prezzo_finale_euro,
    }))
  }

  const handlePrezzoFinaleBlur = (value) => {
    const nuovoPrezzo = euroToCents(value)
    if (nuovoPrezzo !== null) {
      setFormData(prev => ({ ...prev, prezzo_finale_euro: centsToEuroInput(nuovoPrezzo) }))
    }
  }

  // Il prezzo della spedizione è addebitato una sola volta per ordine.
  const totaleProdottiCents = useMemo(() => {
    const totaleRighe = formData.righe.reduce((acc, riga) => {
      const prezzo = riga.prezzo_unitario_cents || 0
      const subtotale = prezzo * riga.quantita
      return acc + subtotale - Math.trunc(subtotale * Number(riga.sconto_percentuale || 0) / 100)
    }, 0)
    
    // Per Tipo 2 (Box Personalizzata), moltiplica per quantità box
    if (formData.tipo === 2) {
      const subtotale = (prezzoFinaleCents || 0) * formData.quantita_box
      return subtotale - Math.trunc(subtotale * Number(formData.sconto_riga_box_percentuale || 0) / 100)
    }
    return totaleRighe
  }, [formData.righe, formData.tipo, formData.quantita_box, formData.sconto_riga_box_percentuale, prezzoFinaleCents])
  const spedizioneCents = euroToCents(formData.spedizione_euro)
  const totaleLordoCents = spedizioneCents === null ? null : totaleProdottiCents + spedizioneCents
  const totaleInseritoCents = formData.totale_finale_euro === null
    ? null : euroToCents(formData.totale_finale_euro)
  const scontoOrdineCents = formData.totale_finale_euro === null
    ? Number(formData.sconto_importo_cents || 0)
    : totaleLordoCents === null || totaleInseritoCents === null
      ? null : totaleLordoCents - totaleInseritoCents
  const totaleOrdineCents = totaleLordoCents === null || scontoOrdineCents === null
    ? null : totaleLordoCents - scontoOrdineCents
  const totaleFinaleInput = formData.totale_finale_euro ?? centsToEuroInput(totaleOrdineCents)
  const giaSaldatoCents = (ordineEsistente?.totale_pagato_cents || 0)
    + (ordineEsistente?.sconti_pagamento_cents || 0)
  const scontiPagamentoCents = ordineEsistente?.sconti_pagamento_cents || 0
  const totaleValido = totaleOrdineCents !== null && scontoOrdineCents >= 0
    && totaleOrdineCents >= giaSaldatoCents

  const costoMerceCents = (() => {
    if (formData.tipo === 2) {
      if (costoTotaleCents === null || formData.righe.some((riga) => {
        const prodotto = prodotti.find((p) => p.id === riga.prodotto) || riga.prodotto_data
        return riga.prodotto && prodotto?.prezzo_acquisto_cents == null
      })) return null
      return costoTotaleCents * formData.quantita_box
    }
    return formData.righe.reduce((totale, riga) => {
      if (totale === null) return null
      const costoUnitario = riga.tipologia_box
        ? riga.tipologia_box_data?.costo_totale_cents
        : riga.prodotto_data?.prezzo_acquisto_cents
      return costoUnitario == null ? null : totale + Number(costoUnitario) * riga.quantita
    }, 0)
  })()
  const costoCorriereCents = (ordineEsistente?.spedizioni || []).reduce(
    (totale, spedizione) => totale + Number(spedizione.costo_spedizione_cents || 0), 0
  )
  const costoOrdineCents = costoMerceCents === null ? null : costoMerceCents + costoCorriereCents
  const ricavoNettoCents = totaleOrdineCents === null ? null : totaleOrdineCents - scontiPagamentoCents
  const margineOrdineCents = totaleOrdineCents === null || costoOrdineCents === null
    ? null : ricavoNettoCents - costoOrdineCents

  const handleTotaleFinaleBlur = (value) => {
    const nuovoTotale = euroToCents(value)
    if (nuovoTotale === null || totaleLordoCents === null ||
        nuovoTotale > totaleLordoCents || nuovoTotale < giaSaldatoCents) return
    setFormData(prev => ({
      ...prev,
      sconto_importo_cents: totaleLordoCents - nuovoTotale,
      totale_finale_euro: null,
    }))
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(value || 0)
  }

  // Filtra righe per tipo (box vs prodotti)
  const righeBox = formData.righe.filter(r => r.tipologia_box)
  const righeProdotti = formData.righe.filter(r => r.prodotto)

  const isStepComplete = (stepId) => {
    switch (stepId) {
      case 1: return formData.cliente !== null && formData.venditore !== null
      case 2: return formData.tipo !== null
      case 3: {
        // Tipo 1: serve almeno una riga box
        if (formData.tipo === 1) return formData.righe.length > 0
        // Tipo 2: serve scatola + almeno un prodotto
        if (formData.tipo === 2) {
          if (isEditMode) return !!(formData.configurazione_scatola && formData.righe.length > 0)
          return !!(
            formData.configurazione_scatola && formData.righe.length > 0 &&
            costoScatolaCents !== null && prezzoFinaleCents !== null &&
            prezzoFinaleCents > 0 && prezzoFinaleCents >= costoTotaleCents
          )
        }
        return false
      }
      case 4: return true
      default: return false
    }
  }

  const canGoToStep = (target) => {
    if (target <= step) return true
    for (let previous = 1; previous < target; previous += 1) {
      if (!isStepComplete(previous)) return false
    }
    return true
  }

  const canSubmit = () => spedizioneCents !== null && totaleValido && [1, 2, 3].every(isStepComplete)

  const handleSubmit = () => {
    if (spedizioneCents === null) {
      toast.error('Inserisci un importo di spedizione valido, anche 0,00 se gratuito')
      return
    }
    if (!totaleValido) {
      toast.error('Il totale deve essere valido, non superare prodotti e spedizione né essere inferiore a quanto già saldato')
      return
    }
    // Pre-check range consegna richiesta
    const dal = formData.data_consegna_richiesta_dal
    const al = formData.data_consegna_richiesta_al
    if (dal && al && al < dal) {
      toast.error('La data "al" deve essere uguale o successiva alla data "dal"')
      return
    }

    const dataToSend = {
      cliente: formData.cliente?.id,
      venditore: formData.venditore?.id || null,
      indirizzo_spedizione: formData.indirizzo_spedizione,
      tipo: formData.tipo,
      configurazione_scatola: formData.configurazione_scatola,
      note: formData.note,
      note_interne: formData.note_interne,
      data_consegna_richiesta_dal: formData.data_consegna_richiesta_dal || null,
      data_consegna_richiesta_al: formData.data_consegna_richiesta_al || null,
      richiede_brandizzazione: formData.richiede_brandizzazione,
      note_brandizzazione: formData.note_brandizzazione,
      totale_spedizione_cents: spedizioneCents,
      sconto_percentuale: 0,
      sconto_importo_cents: scontoOrdineCents,
      righe: formData.righe.map(riga => ({
        tipologia_box: riga.tipologia_box,
        prodotto: riga.prodotto,
        quantita: riga.quantita,
        sconto_percentuale: riga.sconto_percentuale || 0,
      })).filter(r => r.tipologia_box || r.prodotto)
    }

    // Campi dedicati SOLO alla creazione ordine tipo 2 (box personalizzata).
    if (!isEditMode && formData.tipo === 2) {
      dataToSend.quantita_box = formData.quantita_box
      dataToSend.costo_scatola_cents = costoScatolaCents
      dataToSend.prezzo_finale_cents = prezzoFinaleCents
      dataToSend.salva_come_template = salvaComeBox
      dataToSend.nome_template = salvaComeBox ? nomeBox : null
      dataToSend.codice_template = salvaComeBox ? codiceBox : null
    }

    if (isEditMode && formData.tipo === 2) {
      // L'ordine contiene una sola riga box: i prodotti sono la sua composizione,
      // non righe ordine da sostituire durante la modifica.
      delete dataToSend.righe
      dataToSend.quantita_box = formData.quantita_box
    }

    if (isEditMode && formData.tipo !== 2) {
      const righeIniziali = initialEditForm.current?.righe || []
      const righeInvariate = formData.righe.length === righeIniziali.length &&
        formData.righe.every((riga, index) => {
          const iniziale = righeIniziali[index]
          return riga.id === iniziale.id && riga.quantita === iniziale.quantita &&
            Number(riga.sconto_percentuale || 0) === Number(iniziale.sconto_percentuale || 0)
        })
      if (righeInvariate) delete dataToSend.righe
    }

    if (isEditMode) {
      // Modalità modifica
      updateMutation.mutate({ id, data: dataToSend }, {
        onSuccess: () => {
          clearOrderWizardDraft(draftKey)
          navigate(`/ordini/${id}`)
        },
        onError: (error) => {
          console.error('❌ Errore PATCH:', error)
          console.error('❌ Response:', error.response?.data)
          // Mostra messaggio di errore specifico
          const errorData = error.response?.data
          if (errorData?.non_field_errors) {
            alert(errorData.non_field_errors.join('\n'))
          } else if (typeof errorData === 'object') {
            const messages = Object.entries(errorData)
              .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
              .join('\n')
            alert(messages || 'Errore durante il salvataggio')
          } else {
            alert('Errore durante il salvataggio dell\'ordine')
          }
        }
      })
    } else {
      // Modalità creazione
      createMutation.mutate(dataToSend, {
        onSuccess: (response) => {
          clearOrderWizardDraft(draftKey)
          const ordineId = response.data?.id || response.id
          if (ordineId) {
            navigate(`/ordini/${ordineId}`)
          } else {
            navigate('/ordini')
          }
        }
      })
    }
  }
  
  // Loading state per modalità edit
  if (isEditMode && loadingOrdine) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Caricamento ordine...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(isEditMode ? `/ordini/${id}` : '/ordini')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {isEditMode ? `Modifica Ordine #${ordineEsistente?.numero || id}` : 'Nuovo Ordine'}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Modifica i dati dell\'ordine' : 'Creazione guidata ordine'}
          </p>
        </div>
        {draftReady && (isEditMode || step > 1 || formData.cliente || formData.righe.length > 0) && (
          <Button type="button" variant="outline" className="ml-auto" onClick={() => setPendingReset({ kind: 'restart' })}>
            {isEditMode ? 'Scarta modifiche' : 'Ricomincia'}
          </Button>
        )}
      </div>

      {/* Progress Steps */}
      <nav className="flex flex-wrap items-center gap-3" aria-label="Passaggi ordine">
        {STEPS.map((s, index) => {
          const Icon = s.icon
          const isActive = step === s.id
          const isCompleted = step > s.id
          
          return (
            <div key={s.id} className="flex items-center">
              <button type="button" onClick={() => setStep(s.id)} disabled={!canGoToStep(s.id)}
                aria-current={isActive ? 'step' : undefined}
                className="flex items-center gap-2 rounded-md p-1 text-left disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
              <span className={`
                flex items-center justify-center w-10 h-10 rounded-full border-2
                ${isActive ? 'border-primary bg-primary text-primary-foreground' : ''}
                ${isCompleted ? 'border-green-500 bg-green-500 text-white' : ''}
                ${!isActive && !isCompleted ? 'border-muted-foreground/30 text-muted-foreground' : ''}
              `}>
                {isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
              </span>
              <span className={`text-sm font-medium ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                {s.title}
              </span>
              </button>
              {index < STEPS.length - 1 && (
                <div aria-hidden="true" className={`w-8 h-0.5 mx-1 ${isCompleted ? 'bg-green-500' : 'bg-muted'}`} />
              )}
            </div>
          )
        })}
      </nav>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Step 1: Selezione Cliente */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label>Cerca Cliente</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Inizia a digitare per cercare un cliente esistente o creane uno nuovo
                </p>
                <ClienteAutocomplete
                  onSelect={handleSelectCliente}
                  onCreateNew={() => setCreateClienteDialogOpen(true)}
                  selectedCliente={formData.cliente}
                  placeholder="Cerca per nome, email o P.IVA..."
                />
              </div>

              {formData.cliente && (
                <div className="p-4 border rounded-lg bg-primary/5">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{formData.cliente.nome_completo}</p>
                      <p className="text-sm text-muted-foreground">{formData.cliente.email}</p>
                      {formData.cliente.telefono && (
                        <p className="text-sm text-muted-foreground">{formData.cliente.telefono}</p>
                      )}
                    </div>
                    <Badge variant="success">✓ Selezionato</Badge>
                  </div>
                </div>
              )}

              <Separator />

              {/* Selezione Venditore */}
              <div>
                <Label>Venditore *</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Seleziona il venditore associato all'ordine per le provvigioni
                </p>
                <VenditoreSelector
                  value={formData.venditore}
                  onChange={(venditore) => setFormData(prev => ({ ...prev, venditore }))}
                  error={!formData.venditore && formData.cliente ? 'Seleziona un venditore per continuare' : null}
                />
              </div>
            </div>
          )}

          {/* Step 2: Tipo Ordine */}
          {step === 2 && (
            <div className="grid gap-4 md:grid-cols-2">
              {TIPI_ORDINE.map((tipo) => (
                <div
                  key={tipo.id}
                  onClick={() => handleSelectTipo(tipo.id)}
                  className={`
                    p-4 border rounded-lg transition-all ${isEditMode ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}
                    ${formData.tipo === tipo.id ? 'border-primary bg-primary/5 ring-2 ring-primary' : 'hover:bg-accent'}
                  `}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{tipo.icon}</span>
                    <div>
                      <p className="font-medium">{tipo.nome}</p>
                      <p className="text-sm text-muted-foreground">{tipo.descrizione}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 3: Prodotti/Box - Dinamico per tipo ordine */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Tipo 1: Box da Catalogo + Prodotti Singoli */}
              {formData.tipo === 1 && (
                <div className="space-y-6">
                  {/* Box da Catalogo */}
                  <div>
                    <div className="mb-4">
                      <h3 className="text-lg font-medium">Box dal Catalogo</h3>
                      <p className="text-sm text-muted-foreground">
                        Scegli le box predefinite con composizione fissa
                      </p>
                    </div>
                    <BoxSelector
                      selectedItems={righeBox}
                      onSelect={handleSelectBox}
                      onUpdateQuantity={handleUpdateBoxQuantity}
                      onRemove={handleRemoveBox}
                    />
                  </div>

                  {/* Prodotti Singoli */}
                  <div>
                    <div className="mb-4">
                      <h3 className="text-lg font-medium">Prodotti Singoli</h3>
                      <p className="text-sm text-muted-foreground">
                        Aggiungi prodotti vendibili singolarmente (es. panettoni, vini particolari)
                      </p>
                    </div>
                    <ProdottoSelector
                      selectedItems={righeProdotti}
                      onSelect={handleSelectProdotto}
                      onUpdateQuantity={handleUpdateProdottoQuantity}
                      onRemove={handleRemoveProdotto}
                    />
                  </div>
                </div>
              )}

              {/* Tipo 2: Box Personalizzata */}
              {formData.tipo === 2 && (
                <div className="space-y-6">
                  {/* Selezione Scatola */}
                  <div>
                    <div className="mb-4">
                      <h3 className="text-lg font-medium">1. Scegli la Scatola</h3>
                      <p className="text-sm text-muted-foreground">
                        Seleziona la configurazione della scatola fisica
                      </p>
                    </div>
                    <select
                      value={formData.configurazione_scatola || ''}
                      disabled={isEditMode}
                      onChange={(e) => handleSelectConfigurazione(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full border rounded-md px-3 py-2"
                      required
                    >
                      <option value="">-- Seleziona una scatola --</option>
                      {configurazioni.map((config) => (
                        <option key={config.id} value={config.id}>
                          {config.nome} - {config.costo_totale ? `€${config.costo_totale}` : 'N/D'}
                          {config.dimensioni_interne ? ` (${config.dimensioni_interne})` : ''}
                        </option>
                      ))}
                    </select>
                    {formData.configurazione_scatola && (() => {
                      const scatolaSelezionata = configurazioni.find(c => c.id === formData.configurazione_scatola)
                      return (
                        <div className="mt-2 p-3 bg-muted rounded-md">
                          <p className="text-sm font-medium">✓ Scatola: {scatolaSelezionata?.nome}</p>
                          <p className="text-xs text-muted-foreground">
                            Costo scatola: {scatolaSelezionata?.costo_totale ? `€${scatolaSelezionata.costo_totale}` : 'N/D'}
                          </p>
                        </div>
                      )
                    })()}
                  </div>
                  
                  {/* Selezione Template (opzionale) */}
                  {formData.configurazione_scatola && tipologiePerScatola.length > 0 && (
                    <div>
                      <div className="mb-4">
                        <h3 className="text-lg font-medium">2. Usa un Template (opzionale)</h3>
                        <p className="text-sm text-muted-foreground">
                          Parti da una box esistente per precompilare i prodotti
                        </p>
                      </div>
                      <select
                        value={formData.template_box || ''}
                        disabled={isEditMode}
                        onChange={(e) => handleSelectTemplateBox(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full border rounded-md px-3 py-2"
                      >
                        <option value="">-- Nessun template (componi da zero) --</option>
                        {tipologiePerScatola.map((tipologia) => (
                          <option key={tipologia.id} value={tipologia.id}>
                            {tipologia.nome} - {tipologia.num_prodotti || 0} prodotti
                          </option>
                        ))}
                      </select>
                      {formData.template_box && (
                        <p className="text-xs text-success mt-2">
                          {isEditMode ? '✓ Composizione della box salvata' : '✓ Prodotti precompilati dal template (puoi modificarli)'}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <Separator />
                  
                  {/* Selezione Prodotti */}
                  <div>
                    <div className="mb-4">
                      <h3 className="text-lg font-medium">{formData.template_box ? '3' : '2'}. Seleziona i Prodotti</h3>
                      <p className="text-sm text-muted-foreground">
                        {isEditMode
                          ? 'Composizione della box già creata (sola lettura). Puoi modificare il numero di box qui sotto.'
                          : formData.template_box
                            ? 'Modifica i prodotti precompilati o aggiungine di nuovi'
                            : 'Componi la box scegliendo i prodotti da inserire'}
                      </p>
                    </div>
                    {isEditMode ? (
                      <div className="space-y-2 rounded-lg border p-4">
                        {righeProdotti.map((riga) => (
                          <div key={riga.prodotto} className="flex justify-between text-sm">
                            <span>{riga.prodotto_data?.nome || `Prodotto #${riga.prodotto}`}</span>
                            <span>x{riga.quantita} per box</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <ProdottoSelector
                        selectedItems={righeProdotti}
                        onSelect={handleSelectProdotto}
                        onUpdateQuantity={handleUpdateProdottoQuantity}
                        onRemove={handleRemoveProdotto}
                        filterVendibileSingolarmente={false}
                      />
                    )}
                  </div>
                  
                  {/* Prezzi della singola box, prima della quantità da assemblare */}
                  {formData.configurazione_scatola && righeProdotti.length > 0 && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <h3 className="text-lg font-medium">Prezzo e Margini per Box</h3>
                        {isEditMode && (
                          <p className="text-sm text-muted-foreground">
                            I prezzi di un ordine già creato sono visualizzati qui; la modifica richiede
                            l'aggiornamento della box e delle riserve dell'ordine.
                          </p>
                        )}
                        <div className="grid gap-4 md:grid-cols-5">
                          <div className="space-y-2">
                            <Label htmlFor="costo_scatola_custom">Costo scatola (€)</Label>
                            <Input
                              id="costo_scatola_custom"
                              type="text"
                              inputMode="decimal"
                              value={costoScatolaInput}
                              onFocus={handleCostoScatolaFocus}
                              onChange={(e) => handleCostoScatolaChange(e.target.value)}
                              onBlur={(e) => handleCostoScatolaBlur(e.target.value)}
                              disabled={isEditMode}
                            />
                            <p className="text-xs text-muted-foreground">Costo specifico di questa box</p>
                          </div>
                          <div className="p-3 bg-muted rounded-lg">
                            <p className="text-xs text-muted-foreground">Costo contenuto</p>
                            <p className="font-medium">{formatCurrency(costoContenutoCents / 100)}</p>
                          </div>
                          <div className="p-3 bg-muted rounded-lg">
                            <p className="text-xs text-muted-foreground">Costo totale</p>
                            <p className="font-medium">{costoTotaleCents === null ? '—' : formatCurrency(costoTotaleCents / 100)}</p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="prezzo_finale_custom">Prezzo finale per box (€)</Label>
                            <Input
                              id="prezzo_finale_custom"
                              type="text"
                              inputMode="decimal"
                              value={prezzoFinaleInput}
                              onChange={(e) => setFormData(prev => ({ ...prev, prezzo_finale_euro: e.target.value }))}
                              onBlur={(e) => handlePrezzoFinaleBlur(e.target.value)}
                              disabled={isEditMode}
                            />
                            <p className="text-xs text-muted-foreground">Importo in euro, es. 25,50</p>
                          </div>
                          <div className={`p-3 rounded-lg ${margineCents === null ? 'bg-muted' : margineCents >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                            <p className="text-xs text-muted-foreground">Margine</p>
                            <p className={`font-medium ${margineCents === null ? '' : margineCents >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {margineCents === null ? '—' : `${formatCurrency(margineCents / 100)} (${prezzoFinaleCents ? ((margineCents / prezzoFinaleCents) * 100).toFixed(2) : '0.00'}%)`}
                            </p>
                          </div>
                        </div>
                        {costoScatolaCents === null || prezzoFinaleCents === null ? (
                          <p className="text-sm text-red-600">Inserisci importi validi in euro, con massimo due decimali.</p>
                        ) : prezzoFinaleCents < costoTotaleCents ? (
                          <p className="text-sm text-red-600">Il prezzo finale deve coprire almeno il costo totale.</p>
                        ) : null}
                      </div>
                    </>
                  )}

                  {/* Quantità Box da creare */}
                  {righeProdotti.length > 0 && (
                    <>
                      <Separator />
                      <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                        <div className="mb-3">
                          <h3 className="text-lg font-medium">{formData.template_box ? '4' : '3'}. Quante Box vuoi creare?</h3>
                          <p className="text-sm text-muted-foreground">
                            Ogni box conterrà i prodotti selezionati sopra
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setFormData(prev => ({ 
                              ...prev, 
                              quantita_box: Math.max(1, prev.quantita_box - 1) 
                            }))}
                            disabled={formData.quantita_box <= 1}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <div className="text-center min-w-[80px]">
                            <Input
                              type="number"
                              min="1"
                              value={formData.quantita_box}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                quantita_box: Math.max(1, parseInt(e.target.value) || 1)
                              }))}
                              className="w-20 text-center text-2xl font-bold h-10"
                            />
                            <p className="text-xs text-muted-foreground mt-1">box</p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setFormData(prev => ({ 
                              ...prev, 
                              quantita_box: prev.quantita_box + 1 
                            }))}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        {formData.quantita_box > 1 && (
                          <p className="text-sm text-primary mt-3">
                            📦 Verranno create {formData.quantita_box} box identiche, ciascuna con {righeProdotti.length} prodotti
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Brandizzazione Opzionale (per entrambi i tipi) */}
              <Separator />
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="richiede_brandizzazione"
                    checked={formData.richiede_brandizzazione}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      richiede_brandizzazione: e.target.checked,
                      note_brandizzazione: e.target.checked ? prev.note_brandizzazione : ''
                    }))}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="richiede_brandizzazione" className="text-base font-medium cursor-pointer">
                    🏷️ Richiedi Brandizzazione Personalizzata
                  </Label>
                </div>
                
                {formData.richiede_brandizzazione && (
                  <div>
                    <Label htmlFor="note_brandizzazione">Dettagli Brandizzazione</Label>
                    <Textarea
                      id="note_brandizzazione"
                      placeholder="Descrivi la personalizzazione desiderata: logo, colori, posizione stampa, materiali..."
                      value={formData.note_brandizzazione}
                      onChange={(e) => setFormData(prev => ({ ...prev, note_brandizzazione: e.target.value }))}
                      rows={4}
                      className="mt-2"
                    />
                  </div>
                )}
              </div>

              {/* Totale Stimato */}
              {formData.righe.length > 0 && (
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Totale prodotti stimato:</span>
                    <span className="text-2xl font-bold">{formatCurrency(totaleProdottiCents / 100)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.righe.length} {formData.righe.length === 1 ? 'articolo' : 'articoli'} selezionati
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Riepilogo */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground">Cliente</Label>
                  <p className="font-medium">{formData.cliente?.nome_completo}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Tipo Ordine</Label>
                  <p className="font-medium">
                    {TIPI_ORDINE.find(t => t.id === formData.tipo)?.nome}
                  </p>
                </div>
              </div>

              {/* Riepilogo Box Personalizzata (Tipo 2) */}
              {formData.tipo === 2 && (
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-lg">📦 Box Personalizzata</p>
                      <p className="text-sm text-muted-foreground">
                        {righeProdotti.length} prodotti per box
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold">{formData.quantita_box}</p>
                      <p className="text-sm text-muted-foreground">
                        {formData.quantita_box === 1 ? 'box' : 'box identiche'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-muted-foreground">
                  {formData.tipo === 2 ? 'Contenuto di ogni Box' : 'Righe Ordine'}
                </Label>
                <div className="mt-2 space-y-2">
                  {formData.righe.map((riga, index) => {
                    const nome = riga.tipologia_box_data?.nome || riga.prodotto_data?.nome || `ID: ${riga.tipologia_box || riga.prodotto}`
                    const prezzo = (riga.prezzo_unitario_cents || 0) / 100
                    return (
                      <div key={index} className="flex justify-between p-3 bg-muted rounded">
                        <div>
                          <span className="font-medium">{nome}</span>
                          <span className="text-sm text-muted-foreground ml-2">x{riga.quantita}</span>
                        </div>
                        <span className="font-medium">{formatCurrency(prezzo * riga.quantita)}</span>
                      </div>
                    )
                  })}
                </div>
                <Separator className="my-3" />
                <div className="flex justify-between text-sm">
                  <span>Totale prodotti</span>
                  <span>{formatCurrency(totaleProdottiCents / 100)}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor="spedizione_euro">Spedizione addebitata al cliente (€)</Label>
                    <p className="text-xs text-muted-foreground">Importo per l'intero ordine; usa 0,00 per spedizione gratuita o ritiro in sede.</p>
                  </div>
                  <Input
                    id="spedizione_euro"
                    type="text"
                    inputMode="decimal"
                    value={formData.spedizione_euro}
                    onChange={(event) => setFormData(prev => ({ ...prev, spedizione_euro: event.target.value }))}
                    onBlur={() => {
                      if (spedizioneCents !== null) {
                        setFormData(prev => ({ ...prev, spedizione_euro: centsToEuroInput(spedizioneCents) }))
                      }
                    }}
                    className="w-32 text-right"
                    aria-invalid={spedizioneCents === null}
                  />
                </div>
                {spedizioneCents === null && (
                  <p className="text-sm text-destructive">Inserisci un importo valido, per esempio 10,50 o 0,00.</p>
                )}
                <div className="flex justify-between text-sm">
                  <span>Sconto sull'ordine</span>
                  <span>{scontoOrdineCents === null ? '—' : scontoOrdineCents > 0
                    ? `−${formatCurrency(scontoOrdineCents / 100)}` : formatCurrency(0)}</span>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-lg border p-3 bg-muted/40">
                    <p className="text-xs text-muted-foreground">Costo stimato</p>
                    <p className="text-xl font-semibold">
                      {costoOrdineCents === null ? '—' : formatCurrency(costoOrdineCents / 100)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {costoCorriereCents > 0 ? 'Merce + costo corriere registrato' : 'Merce ai costi attuali; corriere escluso'}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3 bg-muted/40">
                    <p className="text-xs text-muted-foreground">Totale di vendita</p>
                    <p className="text-xl font-semibold">
                      {totaleOrdineCents === null ? '—' : formatCurrency(totaleOrdineCents / 100)}
                    </p>
                    <p className="text-xs text-muted-foreground">Prodotti + spedizione, dopo lo sconto sull'ordine</p>
                  </div>
                  <div className={`rounded-lg border p-3 ${margineOrdineCents === null ? 'bg-muted/40' : margineOrdineCents >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                    <p className="text-xs text-muted-foreground">Margine stimato</p>
                    <p className={`text-xl font-semibold ${margineOrdineCents === null ? '' : margineOrdineCents >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {margineOrdineCents === null ? '—' : `${formatCurrency(margineOrdineCents / 100)} (${ricavoNettoCents > 0 ? `${((margineOrdineCents / ricavoNettoCents) * 100).toFixed(2)}%` : '—'})`}
                    </p>
                    <p className="text-xs text-muted-foreground">Sul ricavo dopo tutti gli sconti</p>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-4 pt-2">
                  <div>
                    <Label htmlFor="totale_finale_euro">Totale finale da pagare (€)</Label>
                    <p className="text-xs text-muted-foreground">Modificalo per applicare uno sconto all'intero ordine.</p>
                  </div>
                  <Input
                    id="totale_finale_euro"
                    type="text"
                    inputMode="decimal"
                    value={totaleFinaleInput}
                    onChange={(event) => setFormData(prev => ({ ...prev, totale_finale_euro: event.target.value }))}
                    onBlur={(event) => handleTotaleFinaleBlur(event.target.value)}
                    className="w-36 text-right text-lg font-bold"
                    aria-invalid={!totaleValido}
                  />
                </div>
                {!totaleValido && (
                  <p className="text-sm text-destructive">
                    Il totale deve essere tra {formatCurrency(giaSaldatoCents / 100)} e {totaleLordoCents === null ? '—' : formatCurrency(totaleLordoCents / 100)}.
                  </p>
                )}
                {giaSaldatoCents > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Già saldato con pagamenti e sconti registrati: {formatCurrency(giaSaldatoCents / 100)}.
                  </p>
                )}
                {scontiPagamentoCents > 0 && (
                  <p className="text-xs text-amber-700">
                    Ci sono già {formatCurrency(scontiPagamentoCents / 100)} di sconti nei Pagamenti: restano applicati oltre allo sconto qui indicato.
                  </p>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="data_consegna_dal">Data Consegna Richiesta (Dal)</Label>
                  <Input
                    id="data_consegna_dal"
                    type="date"
                    value={formData.data_consegna_richiesta_dal}
                    onChange={(e) => setFormData(prev => ({ ...prev, data_consegna_richiesta_dal: e.target.value }))}
                    max={formData.data_consegna_richiesta_al || undefined}
                  />
                </div>
                <div>
                  <Label htmlFor="data_consegna_al">Data Consegna Richiesta (Al)</Label>
                  <Input
                    id="data_consegna_al"
                    type="date"
                    value={formData.data_consegna_richiesta_al}
                    onChange={(e) => setFormData(prev => ({ ...prev, data_consegna_richiesta_al: e.target.value }))}
                    min={formData.data_consegna_richiesta_dal || undefined}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="note">Note Cliente</Label>
                <Textarea
                  id="note"
                  placeholder="Note visibili al cliente..."
                  value={formData.note}
                  onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="note_interne">Note Interne</Label>
                <Textarea
                  id="note_interne"
                  placeholder="Note visibili solo agli operatori..."
                  value={formData.note_interne}
                  onChange={(e) => setFormData(prev => ({ ...prev, note_interne: e.target.value }))}
                  rows={2}
                />
              </div>

              {formData.richiede_brandizzazione && (
                <div>
                  <Label htmlFor="note_brandizzazione_riepilogo">Note Brandizzazione</Label>
                  <div className="mt-2 p-3 bg-muted rounded">
                    <p className="text-sm whitespace-pre-wrap">
                      {formData.note_brandizzazione || 'Nessuna nota specificata'}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Opzione Salva come TipologiaBox (solo per Tipo 2) */}
              {!isEditMode && formData.tipo === 2 && formData.righe.length > 0 && (
                <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950">
                  <div className="flex items-center space-x-2 mb-3">
                    <input
                      type="checkbox"
                      id="salva_come_box"
                      checked={salvaComeBox}
                      onChange={(e) => setSalvaComeBox(e.target.checked)}
                      className="h-4 w-4"
                    />
                    <Label htmlFor="salva_come_box" className="text-base font-medium cursor-pointer">
                      💾 Salva questa composizione come TipologiaBox nel catalogo
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Utile se il cliente riordinerà questa stessa box in futuro
                  </p>
                  {salvaComeBox && (
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <Label htmlFor="codice_box" className="text-sm">Codice Box *</Label>
                        <Input
                          id="codice_box"
                          placeholder="es. BOX-CLIENTE-001"
                          value={codiceBox}
                          onChange={(e) => setCodiceBox(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="nome_box" className="text-sm">Nome Box *</Label>
                        <Input
                          id="nome_box"
                          placeholder="es. Box Natale Cliente XYZ"
                          value={nomeBox}
                          onChange={(e) => setNomeBox(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setStep(prev => prev - 1)}
          disabled={step === 1}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Indietro
        </Button>

        {step < 4 ? (
          <Button
            onClick={() => setStep(prev => prev + 1)}
            disabled={!isStepComplete(step)}
          >
            Avanti
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending || !canSubmit()}
          >
            {(createMutation.isPending || updateMutation.isPending) 
              ? (isEditMode ? 'Salvataggio...' : 'Creazione...') 
              : (isEditMode ? 'Salva Modifiche' : 'Crea Ordine')}
            <Check className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Dialog Creazione Cliente Rapida */}
      <ClienteQuickCreateDialog
        open={createClienteDialogOpen}
        onOpenChange={setCreateClienteDialogOpen}
        onSuccess={(cliente) => {
          handleSelectCliente(cliente)
          setCreateClienteDialogOpen(false)
        }}
      />

      <AlertDialog open={Boolean(pendingDraft)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Riprendere l’ordine in sospeso?</AlertDialogTitle>
            <AlertDialogDescription>
              È stata trovata una bozza di questa {isEditMode ? 'modifica' : 'creazione'} salvata in questa scheda del browser.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={restartWizard}>Ricomincia</AlertDialogCancel>
            <AlertDialogAction onClick={resumeDraft}>Riprendi bozza</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(pendingReset)} onOpenChange={(open) => { if (!open) setPendingReset(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingReset?.kind === 'restart' ? 'Ricominciare l’ordine?' : 'Cambiare selezione?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingReset?.kind === 'restart'
                ? 'Le modifiche inserite in questa bozza verranno eliminate.'
                : 'La composizione e i prezzi già inseriti verranno azzerati.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReset}>Conferma</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
